import { Builder, BuilderComponent } from '@builder.io/react';
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import DefaultErrorPage from 'next/error';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { registerComponents } from '@/builder';
import B2BBackToLatestArticle from '@/components/afterpay-goes-green/b2b-back-to-latest-article/b2b-back-to-latest-article';
import B2BCategoriesBlock from '@/components/afterpay-goes-green/b2b-categories-block/b2b-categories-block';
import B2BHero from '@/components/afterpay-goes-green/b2b-hero/b2b-hero';
import SocialAndShare from '@/components/business-share-icons/business-share-icons';
import B2BLayout from '@/components/layouts/b2b-layout';
import MetaBlock from '@/components/meta-block/MetaBlock';
import { mapBuilderObjectToCategory } from '@/helpers/BuilderHelper';
import { Locale, localifyLink } from '@/helpers/LangHelper';
import ArticleModel from '@/models/ArticleModel';
import { Article } from '@/types/Article';
import { createArticleStaticPaths, createArticleStaticProps } from '@/utils/Builder';
import B2BRelatedArticles, { B2BRelatedArticlesProps } from '@/components/afterpay-goes-green/b2b-related-articles/b2b-related-articles';

registerComponents();

export async function getStaticProps({ params }: GetStaticPropsContext<any>) {
    return createArticleStaticProps({ params });
}

export async function getStaticPaths() {
    return createArticleStaticPaths(false, Locale.USA);
}

export default function ArticlePage(ctx: InferGetStaticPropsType<typeof getStaticProps>) {
    const { article, relatedArticles } = ctx;

    const router = useRouter();
    const locale = Locale.USA;

    const [stateArticle, setStateArticle] = useState<Article>(article);

    useEffect(() => {
        let cancelled = false;

        if ((Builder.isPreviewing || Builder.isEditing) && router.query['builder.overrides.article']) {
            ArticleModel.findById(router.query['builder.overrides.article'] as string).then((articleFromBuilder: any) => {
                if (articleFromBuilder && !cancelled) {
                    const mappedArticle = ArticleModel.fromBuilderContent(articleFromBuilder);

                    setStateArticle(mappedArticle);
                }
            });
        } else if (article) {
            setStateArticle(article);
        }

        return () => {
            cancelled = true;
        };
    }, [article, router.query]);

    if (router.isFallback) {
        return <h1>Loading...</h1>;
    }

    const isLive = !Builder.isEditing && !Builder.isPreviewing;
    if (!stateArticle && isLive) {
        return (
            <>
                <Head>
                    <meta name="robots" content="noindex" />
                    <meta name="title"></meta>
                </Head>
                <DefaultErrorPage statusCode={404} />
            </>
        );
    }

    if (!stateArticle) {
        return <h1>Loading...</h1>;
    }

    const leadCategory = mapBuilderObjectToCategory(stateArticle?.categories[0]);

    const relatedArticlesData: B2BRelatedArticlesProps = {
        title: leadCategory?.name,
        description: leadCategory?.description,
        viewAllSeriesText: `View All Articles`,
        viewAllSeriesUrl: localifyLink(`/${locale}${leadCategory.categoryUrl}`, locale),
        articles: relatedArticles,
    };

    let targetUrl = `/${locale}${stateArticle.articleUrl}`;
    if (typeof window !== 'undefined') {
        targetUrl = `${window?.origin || ''}/${locale}${stateArticle.articleUrl}`;
    }

    return (
        <>
            <MetaBlock title={stateArticle.seoTitle ?? stateArticle.title} description={stateArticle.seoDescription} leadImage={stateArticle.leadImage} />

            <B2BLayout page={stateArticle} model="article">
                <B2BHero
                    title={stateArticle.title}
                    titleMaxWidth={540}
                    backgroundImage={{ desktop: stateArticle.leadImage, mobile: stateArticle.imageMobile }}
                    articleCategories={stateArticle.categories}
                    position={stateArticle.position}
                />
                <BuilderComponent model="article" content={stateArticle.builderModel} />
                <B2BCategoriesBlock categories={stateArticle.categories} />
                <SocialAndShare shareUrl={targetUrl} />
                <B2BBackToLatestArticle latestArticleUrl={localifyLink(`/${locale}/business/access`, locale)} />
                <B2BRelatedArticles relatedArticles={relatedArticlesData} />
            </B2BLayout>
        </>
    );
}
